import { ReactNode, useMemo, useRef, useEffect } from 'react';
import { generateHTML } from '@tiptap/core';

import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import {
  SUB_ACTIVITY_INSTRUCTION_SETTINGS,
  ViewProps,
} from './sub-activity.constants';
import * as Styled from './sub-activity-view.style';
import { formatSubActivityLabel } from './sub-activity.utils';
import SettingsPresentComponentMenu from 'approot/activity-builder/settings/present/settings-present-component-menu';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { ttsGetTextBlockSelector } from 'approot/activity-builder/tts/logic/tts-get-text-block-selector';
import TTSMenuItem from 'approot/activity-builder/tts/tts-menu-item';
import TTSButton from 'approot/activity-builder/tts/tts-button';
import { Box } from 'approot/shared/design-system/box/box.style';
import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';

const SubActivityView = ({
  children,
  subActivityDetails,
  ariaLabel,
  componentId,
  componentType,
  withTTSButton,
  additionalMenuItems,
  onClick,
}: ViewProps & {
  additionalMenuItems?: ReactNode;
}) => {
  const { activeComponent, setActiveComponent } =
    useActivityBuilderPresentContext();
  const componentRef = useRef<HTMLDivElement>(null);

  const extensions = useMemo(
    () => getExtensions(SUB_ACTIVITY_INSTRUCTION_SETTINGS),
    []
  );

  const richtext = useMemo(() => {
    return isRichtextEmpty(subActivityDetails?.instructions)
      ? ''
      : generateHTML(subActivityDetails?.instructions, extensions);
  }, [extensions, subActivityDetails]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === ESCAPE &&
        activeComponent &&
        activeComponent.id === componentId &&
        componentRef.current &&
        componentRef.current.contains(document.activeElement)
      ) {
        setActiveComponent(undefined);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeComponent, componentId, setActiveComponent]);

  if (!subActivityDetails) return null;

  const content = (
    <Styled.SubActivityLabelWrapper data-selector="component-question">
      {subActivityDetails.subActivityLabel && (
        <Styled.SubActivityLabel id={`subactivity-${componentId}-label`}>
          {formatSubActivityLabel(subActivityDetails.subActivityLabel)}
        </Styled.SubActivityLabel>
      )}
      {subActivityDetails.areInstructionsVisible && (
        <Styled.SubActivityInstruction
          dangerouslySetInnerHTML={{
            __html: richtext,
          }}
        />
      )}
    </Styled.SubActivityLabelWrapper>
  );

  return (
    <SettingsPresentComponentMenu
      isActive={!!(activeComponent && activeComponent.id === componentId)}
      menuItems={
        !withTTSButton && componentId && componentType ? (
          <Box $gap={8}>
            <TTSMenuItem
              componentId={componentId}
              componentType={componentType}
            />
            {additionalMenuItems}
          </Box>
        ) : null
      }
    >
      {withTTSButton && componentId && componentType && (
        <TTSButton componentId={componentId} componentType={componentType} />
      )}
      <Styled.SubActivity
        id={componentId ? ttsGetTextBlockSelector(componentId) : undefined}
        ref={componentRef}
      >
        {onClick ? (
          <Styled.SubActivityButton
            type="button"
            aria-label={ariaLabel}
            aria-haspopup
            onClick={onClick}
          >
            {content}
          </Styled.SubActivityButton>
        ) : (
          content
        )}

        <Styled.SubActivityContent>{children}</Styled.SubActivityContent>
      </Styled.SubActivity>
    </SettingsPresentComponentMenu>
  );
};

export default SubActivityView;
